import { useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfig } from "@/contexts/ConfigContext";
import FareSummary from "@/components/FareSummary";
import PassengerPicker from "@/components/PassengerPicker";
import CustomerInfoForm from "@/components/CustomerInfoForm";
import TripCloseoutDialog from "@/components/TripCloseoutDialog";
import { MapPin } from "lucide-react";

/**
 * City Taxi (v2.6.5) — previously named "City to City" + had a "Group" tab.
 * v2.6.9 batch BB · added Customer Info capture + Cash Register close-out dialog.
 */
export default function CityRoutePage() {
  const { config } = useConfig();
  const [routeKey, setRouteKey] = useState("");
  const [pax, setPax] = useState(1);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customer, setCustomer] = useState({});
  const [closeoutOpen, setCloseoutOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // v2.6.9 batch BE-3 · only render presets explicitly enabled in admin pricing.
  // `enabled` defaults to true so older configs that don't set the flag still work,
  // but the mongo backfill set every preset EXCEPT 'single-weatherford-local'
  // to enabled=false per spec — admins can flip them back on in Admin → Prices.
  const allRoutes = config?.presets?.single_fares || {};
  const routes = Object.fromEntries(
    Object.entries(allRoutes).filter(([, r]) => r?.enabled !== false)
  );

  const calculate = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post("/fares/city", { route_key: key, passengers: pax });
      setResult({ ...data, _route_key: key });
      setRouteKey(key);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  // v2.6.9 batch BG · No more pre-trip card capture from FareSummary;
  // the Save Trip button just opens the cash-register dialog directly.
  const openCloseout = () => setCloseoutOpen(true);

  const finalizeTrip = async (closeout) => {
    if (!result) return;
    const r = routes[routeKey];
    setSaving(true);
    try {
      const merged = { ...customer };
      const customer_name = `${merged.first_name || ""} ${merged.last_name || ""}`.trim() || null;

      await api.post("/trips", {
        mode: "city",
        origin: "Weatherford, OK",
        destination: r.name,
        miles: r.miles,
        passengers: pax,
        breakdown: result.breakdown,
        subtotal: result.subtotal,
        cc_surcharge: 0,
        total: result.total,
        payment_method: closeout.payment_method,
        tip_amount: closeout.tip_amount,
        cash_tendered: closeout.cash_tendered,
        change_due: closeout.change_due,
        rating: closeout.rating,
        rating_comment: closeout.rating_comment,
        customer_name,
        customer_phone: merged.phone || null,
        customer_email: merged.email || null,
        customer_address: merged.address || null,
      });
      toast.success(`Trip closed out · ${closeout.rating}★`);
      setResult(null); setRouteKey(""); setCustomer({}); setCloseoutOpen(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="City Taxi">
      <CustomerInfoForm value={customer} onChange={setCustomer} testidPrefix="city-cust" className="mb-4" />

      <Card className="bg-zinc-900 border-zinc-800 p-6 mb-4">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-lg bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-amber-400"><MapPin className="h-5 w-5"/></div>
          <div>
            <h2 className="text-lg font-bold">Preset Routes</h2>
            <p className="text-xs text-zinc-400">Tap a route to calculate · taxes &amp; Technology Fee applied automatically</p>
          </div>
        </div>

        <PassengerPicker value={pax} onChange={setPax} label="Passengers" testid="city-pax-input" min={1} max={6} className="mb-4"/>

        <div className="grid grid-cols-1 gap-2">
          {Object.entries(routes).map(([key, r]) => (
            <button
              key={key}
              data-testid={`city-route-${key}`}
              disabled={busy}
              onClick={() => calculate(key)}
              className={`text-left p-4 rounded-lg border transition-all ${routeKey===key ? "bg-amber-400/10 border-amber-400" : "bg-zinc-950 border-zinc-800 hover:border-zinc-600"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-sm">{r.name}</div>
                  <div className="text-xs text-zinc-500">{r.miles} mi</div>
                </div>
                <div className="text-right">
                  {r.price_per_passenger != null && <div className="text-amber-400 font-bold">${r.price_per_passenger}/pax</div>}
                  {r.fare != null && <div className="text-amber-400 font-bold">${r.fare}</div>}
                  {r.fare_fee != null && <div className="text-amber-400 font-bold text-xs">${r.fare_fee} + extras</div>}
                </div>
              </div>
            </button>
          ))}
          {Object.keys(routes).length === 0 && (
            <div className="text-center text-xs text-zinc-500 py-10">No city routes configured. Set them in Admin → Pricing.</div>
          )}
        </div>
      </Card>

      {result && <FareSummary result={result} onSave={openCloseout} hideCollectCard />}

      <TripCloseoutDialog
        open={closeoutOpen}
        onOpenChange={setCloseoutOpen}
        subtotal={result?.subtotal || 0}
        cc_surcharge={result?.cc_surcharge || 0}
        total={result?.total || 0}
        defaultPaymentMethod="cash"
        cardCollected={false}
        onConfirm={finalizeTrip}
        busy={saving}
      />
    </Layout>
  );
}
